import { supabase } from '../../config/supabase';

/**
 * Get all deliveries assigned to a specific driver
 * @param {string} driverId - The driver's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of jobs with delivery information
 */
export const getMyDeliveries = async (driverId, companyId) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_deliveries!inner (
                    id,
                    quote_type,
                    assigned_driver_id,
                    assigned_driver_name,
                    delivery_status,
                    delivery_date,
                    delivery_time,
                    delivery_address,
                    delivery_contact_name,
                    delivery_contact_phone,
                    special_instructions,
                    delivery_notes,
                    delivered_at,
                    tracking_number,
                    carrier
                )
            `)
            .eq('company_id', companyId)
            .eq('job_deliveries.assigned_driver_id', driverId)
            .order('created_at', { ascending: false });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error fetching my deliveries:', error);
        throw error;
    }
};

/**
 * Subscribe to delivery updates for a driver
 * @param {string} companyId - Company ID
 * @param {Function} callback - Callback function when updates occur
 * @returns {RealtimeChannel} Supabase channel
 */
export const subscribeToMyDeliveryUpdates = (companyId, callback) => {
    const channel = supabase
        .channel(`deliveries-${companyId}-${Date.now()}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_deliveries',
            },
            (payload) => {
                console.log('Delivery update:', payload);
                callback(payload);
            }
        )
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `company_id=eq.${companyId}`
            },
            (payload) => {
                console.log('Job update:', payload);
                callback(payload);
            }
        )
        .subscribe();

    return channel;
};

/**
 * Check if a delivery is completed by the driver
 * @param {Object} delivery - The delivery object from job_deliveries
 * @returns {boolean} True if completed
 */
export const isDeliveryCompleted = (delivery) => {
    return delivery?.delivery_status === 'delivered' || delivery?.delivered_at !== null;
};

/**
 * Update delivery status
 * @param {string} deliveryId - The delivery ID
 * @param {string} status - The new status (not_scheduled, scheduled, in_transit, delivered, cancelled)
 * @param {string} deliveryNotes - Optional delivery notes
 * @param {string} userId - User ID updating the status
 * @param {string} userName - User name updating the status
 * @returns {Promise<Object>} Updated delivery
 */
export const updateDeliveryStatus = async (deliveryId, status, deliveryNotes = null, userId = null, userName = null) => {
    try {
        const updateData = {
            delivery_status: status,
        };

        if (deliveryNotes !== null) {
            updateData.delivery_notes = deliveryNotes;
        }

        if (status === 'delivered') {
            updateData.delivered_at = new Date().toISOString();
            if (userId) updateData.delivered_by_user_id = userId;
            if (userName) updateData.delivered_by_name = userName;
        }

        const { data, error } = await supabase
            .from('job_deliveries')
            .update(updateData)
            .eq('id', deliveryId)
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error updating delivery status:', error);
        throw error;
    }
};
